
import * as React from "react";
import { gql, useMutation, ApolloError, MutationHookOptions } from "@apollo/client";
import {
  ResendOtpMutationMutation,
  ResendOtpMutationMutationVariables
} from "../../../../schemaTypes";

export const resendOtpMutation = gql`
  mutation ResendOtpMutation($email: String!) {
    resendOtp(email: $email) {
      errors {
        path
        message
      }
      message
    }
  }
`;

interface Props {
  children: (
    data: {
      resendOtp: (
        values: ResendOtpMutationMutationVariables
      ) => Promise<ResendOtpMutationMutation["resendOtp"] | null>;
      loading: boolean;
      error?: ApolloError;
      countdown: number;
      canResend: boolean;
    }
  ) => React.ReactElement | null;
  options?: MutationHookOptions<ResendOtpMutationMutation, ResendOtpMutationMutationVariables>;
}

export const ResendOtpController: React.FC<Props> = ({ children, options }) => {
  const [mutate, { loading, error }] = useMutation<ResendOtpMutationMutation, ResendOtpMutationMutationVariables>(resendOtpMutation, options);
  const [countdown, setCountdown] = React.useState(0);
  const timerRef = React.useRef<NodeJS.Timeout | null>(null);

  const startCountdown = React.useCallback((seconds: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCountdown(seconds);
    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const resendOtp = async (values: ResendOtpMutationMutationVariables) => {
    try {
      const result = await mutate({ variables: values });
      const errors = result.data?.resendOtp?.errors;
      if (errors && errors.length > 0) {
        const waitError = errors.find((e: { message: string }) => e.message.includes("Please wait"));
        if (waitError) {
          const match = waitError.message.match(/wait (\d+) minute/);
          if (match) {
            const minutes = parseInt(match[1], 10);
            startCountdown(minutes * 60);
          }
        }
      } else {
        startCountdown(240);
      }
      return result.data?.resendOtp ?? null;
    } catch {
      return null;
    }
  };

  return children({ resendOtp, loading, error, countdown, canResend: countdown === 0 });
};